// The asset half of a resource detail page: meters, service history, and the
// button that records a machine leaving the fleet.
//
// Its own file rather than more of ResourceDetailPage, which is already the
// overview, the availability panel, the documents and the schedule. These
// three only render for plant and vehicles, so keeping them separate also
// keeps the page honest about which parts are kind-conditional.
import { useCallback, useEffect, useState } from 'react';
import {
  fleet,
  type Meter,
  type MeterReading,
  type ServiceRecord,
} from '../fleetApi';
import { ApiError } from '../api';
import type { Resource } from '../schedulingApi';
import { formatDMY, todayISO } from '../lib/dates';
import { documents, type Attachment } from '../documentsApi';
import { Modal, StatusBadge } from './ui';
import { ServiceInvoices } from './ServiceInvoices';

const message = (err: unknown, fallback: string) =>
  err instanceof ApiError ? err.message : fallback;

/** en-AU, no decimals unless the reading has one — 214,500 rather than 214500. */
const reading = (value: number | null, unit?: string) =>
  value === null ? '—' : `${value.toLocaleString('en-AU')}${unit ? ` ${unit}` : ''}`;

// --- Meters -----------------------------------------------------------

export function MetersPanel({
  resource,
  onChanged,
}: {
  resource: Resource;
  onChanged: (message: string) => void;
}) {
  const [meters, setMeters] = useState<Meter[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [history, setHistory] = useState<{ meter: Meter; rows: MeterReading[] } | null>(null);

  const load = useCallback(async () => {
    try {
      setMeters((await fleet.listMeters(resource.id)).meters);
      setError(null);
    } catch (err) {
      setError(message(err, 'Could not load the meters.'));
    }
  }, [resource.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function openHistory(meter: Meter) {
    try {
      const result = await fleet.readings(resource.id, meter.id);
      setHistory({ meter, rows: result.readings });
    } catch (err) {
      setError(message(err, 'Could not load that history.'));
    }
  }

  async function makePrimary(meter: Meter) {
    try {
      await fleet.updateMeter(resource.id, meter.id, { isPrimary: true });
      onChanged(`${meter.label} is now the primary meter.`);
      await load();
    } catch (err) {
      setError(message(err, 'Could not change the primary meter.'));
    }
  }

  return (
    <section className="panel">
      {/* A panel heading in a `.row` moves its 12px INSIDE the row, where it
          separates nothing: the row is `items-end`, so a 44px button's bottom
          edge lands exactly on whatever follows. Hence `!mb-0` on the heading
          and `mb-3` on the row — the same 12px a bare <h2> panel gets, now
          below the whole header rather than trapped inside it, so the gap is
          12px whether the header carries a control or not. */}
      <div className="row mb-3">
        <h2 className="!mb-0">Meters</h2>
        <span className="spacer" />
        <button type="button" className="btn-secondary btn-sm" onClick={() => setAdding(true)}>
          Add meter
        </button>
      </div>

      {error && (
        <div className="banner-error mb-3" role="alert">
          {error}
        </div>
      )}

      {meters.length === 0 ? (
        <p className="muted">
          No meters. Add one, hours or an odometer, and this machine can be part of the yard
          walk and the service-due rule.
        </p>
      ) : (
        <ul className="space-y-2">
          {meters.map((meter) => (
            <li
              key={meter.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded border border-gray-200 p-3"
            >
              <span className="font-medium text-gray-900">{meter.label}</span>
              {/* The primary is the one the service-due rule reads, so it is
                  worth saying on the row rather than only in a menu. */}
              {meter.isPrimary && <StatusBadge kind="today" status="available" label="Primary" />}
              <span className="text-gray-700">{reading(meter.currentReading, meter.unit)}</span>
              {meter.currentReading === null && (
                <span className="text-sm text-gray-500">never read</span>
              )}
              <span className="spacer" />
              <button
                type="button"
                className="btn-ghost btn-sm"
                onClick={() => void openHistory(meter)}
              >
                History
              </button>
              {!meter.isPrimary && (
                <button
                  type="button"
                  className="btn-ghost btn-sm"
                  onClick={() => void makePrimary(meter)}
                >
                  Make primary
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {adding && (
        <AddMeterModal
          resource={resource}
          hasMeters={meters.length > 0}
          onClose={() => setAdding(false)}
          onCreated={() => {
            setAdding(false);
            onChanged('Meter added.');
            void load();
          }}
        />
      )}

      {history && (
        <Modal title={`${history.meter.label} · history`} onClose={() => setHistory(null)}>
          {history.rows.length === 0 ? (
            <p className="muted">No readings recorded yet.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {history.rows.map((row) => (
                <li key={row.id} className="flex flex-wrap gap-x-3 border-b border-gray-100 pb-2">
                  <span className="font-medium tabular-nums text-gray-900">
                    {reading(row.reading, history.meter.unit)}
                  </span>
                  <span className="text-gray-600">{formatDMY(row.readingDate)}</span>
                  {/* The snapshot name. Whoever read the meter that morning
                      stays named even after they have left. */}
                  <span className="text-gray-500">{row.enteredByName}</span>
                </li>
              ))}
            </ul>
          )}
        </Modal>
      )}
    </section>
  );
}

function AddMeterModal({
  resource,
  hasMeters,
  onClose,
  onCreated,
}: {
  resource: Resource;
  hasMeters: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [label, setLabel] = useState('Engine hours');
  const [unit, setUnit] = useState<'km' | 'hours'>('hours');
  const [currentReading, setCurrentReading] = useState('');
  const [isPrimary, setIsPrimary] = useState(!hasMeters);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await fleet.createMeter(resource.id, {
        label,
        unit,
        isPrimary,
        currentReading: currentReading.trim() === '' ? null : Number(currentReading),
      });
      onCreated();
    } catch (err) {
      setError(message(err, 'Could not add that meter.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Add a meter"
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn-ghost btn-sm">
            Cancel
          </button>
          <button type="submit" form="add-meter" disabled={busy} className="btn-primary btn-sm">
            {busy ? 'Adding…' : 'Add meter'}
          </button>
        </>
      }
    >
      {error && (
        <div className="banner-error mb-3" role="alert">
          {error}
        </div>
      )}

      <form id="add-meter" onSubmit={submit} noValidate className="legacy-page">
        <label htmlFor="meter-label">
          Name
          <input
            id="meter-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Engine hours, Odometer, PTO hours"
          />
        </label>

        <label htmlFor="meter-unit">
          Reads in
          <select
            id="meter-unit"
            value={unit}
            onChange={(e) => setUnit(e.target.value as 'km' | 'hours')}
          >
            <option value="hours">Hours</option>
            <option value="km">Kilometres</option>
          </select>
        </label>

        <label htmlFor="meter-reading">
          Reading now
          <input
            id="meter-reading"
            inputMode="decimal"
            value={currentReading}
            onChange={(e) => setCurrentReading(e.target.value)}
          />
          <span className="hint">
            Optional. Leaving it blank means "never read", which is different from zero: the
            service-due rule will say it does not know rather than saying the machine is fine.
          </span>
        </label>

        {hasMeters && (
          <label className="check-label-inline">
            <input
              type="checkbox"
              className="form-check"
              checked={isPrimary}
              onChange={(e) => setIsPrimary(e.target.checked)}
            />
            Make this the primary meter
          </label>
        )}
      </form>
    </Modal>
  );
}

// --- Service history --------------------------------------------------

export function ServiceHistoryPanel({
  resource,
  onChanged,
}: {
  resource: Resource;
  onChanged: (message: string) => void;
}) {
  const [records, setRecords] = useState<ServiceRecord[]>([]);
  const [invoices, setInvoices] = useState<Attachment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [logging, setLogging] = useState(false);

  // The panel fetches its own documents, the way MetersPanel fetches its own
  // readings. The documents panel further down the page asks for the same list
  // — one extra GET on an asset page, and the alternative is threading one
  // page's state through two components that are otherwise independent.
  const load = useCallback(async () => {
    try {
      const [history, files] = await Promise.all([
        fleet.serviceRecords(resource.id),
        documents.list('resource', resource.id),
      ]);
      setRecords(history.serviceRecords);
      setInvoices(files.attachments.filter((f) => f.serviceRecordId !== null));
      setError(null);
    } catch (err) {
      setError(message(err, 'Could not load the service history.'));
    }
  }, [resource.id]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="panel">
      <div className="row mb-3">
        <h2 className="!mb-0">Service history</h2>
        <span className="spacer" />
        <button type="button" className="btn-secondary btn-sm" onClick={() => setLogging(true)}>
          Log a service
        </button>
      </div>

      {error && (
        <div className="banner-error mb-3" role="alert">
          {error}
        </div>
      )}

      {records.length === 0 ? (
        <p className="muted">Nothing logged yet.</p>
      ) : (
        <ul className="space-y-2">
          {records.map((record) => (
            <li key={record.id} className="rounded border border-gray-200 p-3">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="font-medium text-gray-900">{formatDMY(record.serviceDate)}</span>
                {!record.isService && (
                  <StatusBadge kind="today" status="unavailable" label="Repair" />
                )}
                {record.reading !== null && (
                  <span className="text-gray-700">at {reading(record.reading)}</span>
                )}
              </div>
              <div className="mt-1 text-gray-800">{record.description}</div>
              {record.provider && <div className="text-sm text-gray-600">{record.provider}</div>}
              {record.notes && <div className="text-sm text-gray-600">{record.notes}</div>}
              <div className="mt-1 text-xs text-gray-500">
                Logged by {record.createdByName}
                {record.nextDueReading !== null && ` · next due at ${reading(record.nextDueReading)}`}
                {record.nextDueDate && ` · next due ${formatDMY(record.nextDueDate)}`}
              </div>
              <ServiceInvoices
                resourceId={resource.id}
                serviceRecordId={record.id}
                invoices={invoices.filter((f) => f.serviceRecordId === record.id)}
                onChanged={() => void load()}
                onError={(m) => setError(m)}
              />
            </li>
          ))}
        </ul>
      )}

      <p className="hint">
        Service history is kept. Entries are corrected rather than deleted, because a maintenance
        record with the awkward parts removed is not a maintenance record.
      </p>

      {logging && (
        <LogServiceModal
          resource={resource}
          onClose={() => setLogging(false)}
          onLogged={() => {
            setLogging(false);
            onChanged('Service logged.');
            void load();
          }}
        />
      )}
    </section>
  );
}

function LogServiceModal({
  resource,
  onClose,
  onLogged,
}: {
  resource: Resource;
  onClose: () => void;
  onLogged: () => void;
}) {
  const [serviceDate, setServiceDate] = useState(todayISO());
  const [description, setDescription] = useState('');
  const [readingAt, setReadingAt] = useState('');
  const [provider, setProvider] = useState('');
  const [notes, setNotes] = useState('');
  const [isService, setIsService] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await fleet.logService(resource.id, {
        serviceDate,
        description,
        reading: readingAt.trim() === '' ? null : Number(readingAt),
        provider: provider.trim() === '' ? null : provider,
        notes: notes.trim() === '' ? null : notes,
        isService,
      });
      onLogged();
    } catch (err) {
      setError(message(err, 'Could not log that service.'));
    } finally {
      setBusy(false);
    }
  }

  const rollsForward =
    isService && resource.serviceIntervalValue !== null && resource.serviceIntervalUnit !== null;

  return (
    <Modal
      title="Log a service"
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn-ghost btn-sm">
            Cancel
          </button>
          <button type="submit" form="log-service" disabled={busy} className="btn-primary btn-sm">
            {busy ? 'Saving…' : 'Log it'}
          </button>
        </>
      }
    >
      {error && (
        <div className="banner-error mb-3" role="alert">
          {error}
        </div>
      )}

      <form id="log-service" onSubmit={submit} noValidate className="legacy-page">
        <label htmlFor="service-date">
          Date
          <input
            id="service-date"
            type="date"
            value={serviceDate}
            onChange={(e) => setServiceDate(e.target.value)}
          />
        </label>

        <label htmlFor="service-description">
          What was done
          <input
            id="service-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="250-hour service: oil, filters, track tension"
          />
        </label>

        <label htmlFor="service-reading">
          Reading at service
          <input
            id="service-reading"
            inputMode="decimal"
            value={readingAt}
            onChange={(e) => setReadingAt(e.target.value)}
          />
          {rollsForward && (
            <span className="hint">
              With a {resource.serviceIntervalValue} {resource.serviceIntervalUnit} interval set,
              this is what the next service target counts from.
            </span>
          )}
        </label>

        <label htmlFor="service-provider">
          Who did it
          <input
            id="service-provider"
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            placeholder="Hartway Diesel"
          />
        </label>

        <label htmlFor="service-notes">
          Notes
          <textarea
            id="service-notes"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>

        <label className="check-label-inline">
          <input
            type="checkbox"
            className="form-check"
            checked={!isService}
            onChange={(e) => setIsService(!e.target.checked)}
          />
          This was a repair, not a service
        </label>
        <span className="hint">
          Repairs are kept in the history but do not count as "last serviced", and do not move the
          next service target.
        </span>
      </form>
    </Modal>
  );
}

// --- Sell -------------------------------------------------------------

export function SellPanel({
  resource,
  onSold,
}: {
  resource: Resource;
  onSold: (message: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [soldDate, setSoldDate] = useState(todayISO());
  const [soldTo, setSoldTo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Already sold: the record shows on the overview, and there is nothing left
  // to do here.
  if (resource.soldDate) return null;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await fleet.sell(resource.id, {
        soldDate,
        soldTo: soldTo.trim() === '' ? null : soldTo,
      });
      setOpen(false);
      onSold('Recorded as sold, and archived.');
    } catch (err) {
      setError(message(err, 'Could not record that sale.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button type="button" className="btn-secondary btn-sm" onClick={() => setOpen(true)}>
        Mark as sold
      </button>

      {open && (
        <Modal
          title="Mark as sold"
          onClose={() => setOpen(false)}
          footer={
            <>
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost btn-sm">
                Cancel
              </button>
              <button type="submit" form="sell" disabled={busy} className="btn-primary btn-sm">
                {busy ? 'Saving…' : 'Mark as sold'}
              </button>
            </>
          }
        >
          {error && (
            <div className="banner-error mb-3" role="alert">
              {error}
            </div>
          )}

          <p className="mb-3 text-sm text-gray-700">
            This archives {resource.label}: it comes off the pickers and the yard walk, and
            anything already booked on it gets flagged for re-planning. Nothing is deleted; the
            history and the documents stay, and so does this record of the sale.
          </p>

          <form id="sell" onSubmit={submit} noValidate className="legacy-page">
            <label htmlFor="sold-date">
              Sold on
              <input
                id="sold-date"
                type="date"
                value={soldDate}
                onChange={(e) => setSoldDate(e.target.value)}
              />
            </label>

            <label htmlFor="sold-to">
              Sold to
              <input
                id="sold-to"
                value={soldTo}
                onChange={(e) => setSoldTo(e.target.value)}
                placeholder="Hartway Plant Hire"
              />
              <span className="hint">Optional.</span>
            </label>
          </form>
        </Modal>
      )}
    </>
  );
}
